import {
  ACTIVITY_FLUSH_MAX_BYTES, ACTIVITY_MAX_ENTRY_EVENTS, ACTIVITY_MAX_ENTRY_TICKS, ACTIVITY_SCHEMA,
  type ActivityViewport, type OutboxEntry,
} from './activity.model'

/** A serialised item with the `t` it carries (the 14-day expiry is by `oldestT`). */
export interface SerialisedItem { json: string; t: number }

export interface EntryIdentity { userId: string; sessionId: string; wall: number; newId: () => string }

const utf8 = new TextEncoder()
/** Real UTF-8 length: ids and names are ASCII today, but the 48 KB cap must not depend on it. */
const jsonBytes = (s: string) => utf8.encode(s).length

/**
 * Groups serialised items into outbox entries under the per-entry limits (300
 * events, 120 ticks, 48 KB of items), so every entry also fits the keepalive
 * budget. Items are never altered: they stay the exact strings created once.
 */
export function buildEntries(events: SerialisedItem[], ticks: SerialisedItem[], who: EntryIdentity): OutboxEntry[] {
  const entries: OutboxEntry[] = []
  let cur: { ev: SerialisedItem[]; tk: SerialisedItem[]; bytes: number } | null = null
  const flush = () => {
    if (!cur || cur.ev.length + cur.tk.length === 0) return
    const all = [...cur.ev, ...cur.tk]
    entries.push({
      id: who.newId(), userId: who.userId, sessionId: who.sessionId, createdAtWall: who.wall,
      oldestT: Math.min(...all.map(i => i.t)),
      events: cur.ev.map(i => i.json), ticks: cur.tk.map(i => i.json), bytes: cur.bytes,
      attempts: 0, nextAttemptAt: 0,
    })
    cur = null
  }
  const add = (item: SerialisedItem, kind: 'ev' | 'tk') => {
    const size = jsonBytes(item.json)
    const max = kind === 'ev' ? ACTIVITY_MAX_ENTRY_EVENTS : ACTIVITY_MAX_ENTRY_TICKS
    if (cur && (cur[kind].length >= max || cur.bytes + size > ACTIVITY_FLUSH_MAX_BYTES)) flush()
    cur ??= { ev: [], tk: [], bytes: 0 }
    cur[kind].push(item)
    cur.bytes += size
  }
  for (const e of events) add(e, 'ev')
  for (const t of ticks) add(t, 'tk')
  flush()
  return entries
}

/** The request body; the envelope is rebuilt per attempt, the item strings are untouched. */
export function buildBody(
  entry: Pick<OutboxEntry, 'sessionId' | 'events' | 'ticks'>,
  appVersion: string, viewport: ActivityViewport, sentAt: number,
): string {
  return `{"schema":${ACTIVITY_SCHEMA},"sessionId":${JSON.stringify(entry.sessionId)},"appVersion":${JSON.stringify(appVersion)},` +
    `"viewport":${JSON.stringify(viewport)},"sentAt":${sentAt},` +
    `"events":[${entry.events.join(',')}],"ticks":[${entry.ticks.join(',')}]}`
}

/**
 * 413: splits an entry into two (halves of events and ticks, items untouched).
 * Returns null for an entry that holds a single item (cannot be split further).
 */
export function splitEntry(entry: OutboxEntry, newId: () => string): [OutboxEntry, OutboxEntry] | null {
  const evLen = entry.events.length
  const n = evLen + entry.ticks.length
  if (n < 2) return null
  const h = Math.ceil(n / 2)
  const mk = (events: string[], ticks: string[]): OutboxEntry => ({
    ...entry, id: newId(), events, ticks,
    bytes: [...events, ...ticks].reduce((s, i) => s + jsonBytes(i), 0),
    attempts: 0, nextAttemptAt: 0,
  })
  return [
    mk(entry.events.slice(0, h), entry.ticks.slice(0, Math.max(0, h - evLen))),
    mk(entry.events.slice(h), entry.ticks.slice(Math.max(0, h - evLen))),
  ]
}
